/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useFeatureAccess } from "@/hooks/auth/use-feature-access";
import { useCurrentUserDivision, useDivision } from "@/hooks/data/division";
import { useOrganizationBudgetRates } from "@/hooks/data/organization/use-organization-budget-rates";
import { FEATURE_FLAGS } from "@/lib/constants/features/flags";
import type { BudgetRates } from "@/lib/business-logic/estimates";
import { useState } from "react";

export interface CostPagePricing {
  /** Whether the org has access to the budgets feature. */
  hasBudgets: boolean;
  /** The budget the user has explicitly picked in the selector (null = Auto). */
  selectedBudgetId: string | null;
  setSelectedBudgetId: (id: string | null) => void;
  /** Resolved budget rates (from the selected budget, or division-default). */
  budgetRates: BudgetRates | null;
  /** Human name of the budget that Auto currently resolves to. */
  resolvedBudgetName: string | null;
  /** The user's active division — drives Auto resolution. */
  divisionId: string | null;
  /** Human name of the user's active division — surfaced in the selector so
   *  the user can see whether Auto is division-scoped or falling back. */
  divisionName: string | null;
  /** True when a specific budget is picked. Row-level resolvers use this to
   *  decide whether a manual per-item price overrides the budget. */
  hasExplicitBudget: boolean;
}

/**
 * One-stop pricing hook for cost list pages (Labor, Materials, Equipment).
 *
 * Replaces per-page duplication that led to the 2026-04-24 Peabody bug where
 * cost pages ignored the user's division and rendered org-default pricing
 * under a misleading "Auto (<name>)" label.
 *
 * Exactly one consumer of `useOrganizationBudgetRates` per page — the hook
 * returns rates, the resolved budget name, the user's division, and a
 * controlled selected-budget state. Pass the whole object down to the page
 * view and to the shared `<CostPageBudgetSelector />`.
 */
export function useCostPagePricing(): CostPagePricing {
  const { allowed: hasBudgets } = useFeatureAccess(
    FEATURE_FLAGS.ORGANIZATION.BUDGETS,
  );
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);
  const { divisionId } = useCurrentUserDivision();
  const { budgetRates, budgetName: resolvedBudgetName } =
    useOrganizationBudgetRates({
      enabled: hasBudgets,
      budgetId: selectedBudgetId ?? undefined,
      divisionId: divisionId ?? undefined,
    });
  // Fetch the division record so we can surface its name in the selector.
  const { division } = useDivision(divisionId ?? null);
  const divisionName = division?.name ?? null;
  return {
    hasBudgets,
    selectedBudgetId,
    setSelectedBudgetId,
    budgetRates: budgetRates ?? null,
    resolvedBudgetName: resolvedBudgetName ?? null,
    divisionId: divisionId ?? null,
    divisionName,
    hasExplicitBudget: Boolean(selectedBudgetId),
  };
}
